/**
 * Utility functions for converting FileData to File objects for S3 upload.
 */
import { FileData } from '../types/training';

/**
 * Convert FileData to File object for upload.
 * 
 * @param fileData - FileData object from the frontend
 * @returns Promise resolving to File object
 */
export async function fileDataToFile(fileData: FileData): Promise<File> {
  // If nativeFile exists, use it directly (original File object)
  if (fileData.nativeFile) {
    return fileData.nativeFile;
  }

  // If only textContent exists, convert it to File
  if (fileData.textContent) {
    const content = fileData.textContent;
    
    // Check if it's a data URL (starts with "data:")
    if (content.startsWith('data:')) {
      // Extract MIME type and base64 data
      const matches = content.match(/^data:([^;]+);base64,(.+)$/);
      if (matches) {
        const mimeType = matches[1];
        const base64Data = matches[2];
        
        // Convert base64 to binary
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        // Create Blob then File
        const blob = new Blob([bytes], { type: mimeType });
        return new File([blob], fileData.name, { type: mimeType });
      } else {
        // Data URL without base64, try to extract MIME type
        const mimeMatch = content.match(/^data:([^;]+)/);
        const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
        
        // Extract data part (after comma)
        const dataPart = content.split(',')[1] || '';
        const binaryString = atob(dataPart);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        const blob = new Blob([bytes], { type: mimeType });
        return new File([blob], fileData.name, { type: mimeType });
      }
    } else {
      // Plain text content
      const blob = new Blob([content], { type: 'text/plain' });
      return new File([blob], fileData.name, { type: 'text/plain' });
    }
  }

  // Fallback: create empty file
  throw new Error(`Cannot convert FileData to File: no nativeFile or textContent available for ${fileData.name}`);
}

